import React, { useState, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Typography,
  Space,
  Button,
  Select,
  DatePicker,
  Tag,
  Progress,
  Badge,
  Dropdown,
  Menu,
  Spin,
  ConfigProvider,
  Tooltip
} from 'antd';
import {
  FilterOutlined
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import dayjs from 'dayjs';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Area,
  Bar,
  ComposedChart,
  Legend
} from 'recharts';
import styles from './DashboardPage.module.css';
import locale from 'antd/locale/vi_VN';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [filterDropdownVisible, setFilterDropdownVisible] = useState(false);

  // Ref cho các bảng chi tiết
  const lowStockRef = useRef<HTMLDivElement>(null);
  const expiringRef = useRef<HTMLDivElement>(null);

  // Hàm xử lý click vào card thống kê
  const handleStatisticClick = (key: string) => {
    if (key === 'low_stock') {
      lowStockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (key === 'expiring') {
      expiringRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Hàm xử lý chuyển hướng đến màn hình tồn kho
  const handleViewAllLowStock = () => {
    navigate('/inventory');
  };

  // Fetch dashboard data
  const { data: dashboardData, error: dashboardError, isLoading: dashboardLoading } = useQuery(
    ['dashboard', dateRange, selectedCategory],
    () => api.get('/api/reports/dashboard', {
      params: {
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD'),
        category: selectedCategory
      }
    }).then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      retry: 3,
      onError: (error) => {
        console.error('Dashboard API error:', error);
      }
    }
  );



  const { data: lowStockData, error: lowStockError, isLoading: lowStockLoading } = useQuery('low-stock', () =>
    api.get('/api/inventory/low-stock').then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      retry: 3,
      onError: (error) => {
        console.error('Low stock API error:', error);
      }
    }
  );

  const { data: expiringData, error: expiringError, isLoading: expiringLoading } = useQuery('expiring', () =>
    api.get('/api/inventory/expiring').then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      retry: 3,
      onError: (error) => {
        console.error('Expiring API error:', error);
      }
    }
  );

  const { data: categoriesData, error: categoriesError } = useQuery('dashboard-categories', () =>
    api.get('/api/master-data/categories').then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      retry: 3,
      onError: (error) => {
        console.error('Categories API error:', error);
      }
    }
  );

  // Fetch monthly revenue data from real API
  const { data: revenueData, error: revenueError, isLoading: revenueLoading } = useQuery(
    ['monthly-revenue', dateRange],
    () => api.get('/api/reports/revenue', {
      params: {
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD')
      }
    }).then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      retry: 3,
      onError: (error) => {
        console.error('Revenue API error:', error);
      }
    }
  );

  const getSummaryStats = () => {
    const statistics = [
      {
        title: 'Tổng số thuốc',
        value: dashboardData?.data?.total_medicines || 0,
        icon: <i className="fas fa-pills" />,
        color: '#3b82f6',
        suffix: 'loại',
        key: 'total_medicines',
      },
      {
        title: 'Tổng lô thuốc',
        value: dashboardData?.data?.total_lots || 0,
        icon: <i className="fas fa-boxes" />,
        color: '#10b981',
        suffix: 'lô',
        key: 'total_lots',
      },
      {
        title: 'Tồn kho thấp',
        value: lowStockData?.data?.filter((item: any) => item.current_stock <= item.min_stock_level).length || 0,
        icon: <i className="fas fa-arrow-down" />,
        color: '#f59e0b',
        suffix: 'loại',
        key: 'low_stock',
      },
      {
        title: 'Sắp hết hạn',
        value: dashboardData?.data?.expiring_count || expiringData?.data?.length || 0,
        icon: <i className="fas fa-arrow-up" />,
        color: '#ef4444',
        suffix: 'lô',
        key: 'expiring',
      }
    ];

    return (
      <div className={styles.statsContainer}>
        <Row gutter={[24, 16]} className={styles.statsRow}>
          {statistics.map((stat, index) => (
            <Col xs={24} sm={12} lg={6} key={index}>
              <Card 
                className={styles.statCard} 
                hoverable
                onClick={() => handleStatisticClick(stat.key)}
                style={{ cursor: 'pointer' }}
              >
                <div className={styles.statContent}>
                  <div className={styles.statIcon} style={{ background: stat.color }}>
                    {React.cloneElement(stat.icon, { style: { fontSize: '24px' } })}
                  </div>
                  <div className={styles.statInfo}>
                    <Statistic
                      title={stat.title}
                      value={stat.value}
                      suffix={stat.suffix}
                      valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                    />
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // Chart data - using real API data, no fallback to sample data
  const stockTrendData = dashboardData?.data?.stock_trend || [];

  // Use real revenue data from dedicated API endpoint, format for chart display
  const monthlyRevenue = (revenueData?.data || []).map((item: any) => ({
    month: item.month,
    revenue: item.revenue || 0,
    stock_in_expenses: item.stock_in_expenses || 0,
    operating_expenses: item.operating_expenses || 0,
    total_expenses: item.total_expenses || 0,
    profit: item.profit || 0
  }));

  // Table columns
  const lowStockColumns = [
    {
      title: 'Tên thuốc',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>Mã: {record.code}</div>
        </div>
      ),
    },
    {
      title: 'Tồn kho hiện tại',
      dataIndex: 'current_stock',
      key: 'current_stock',
      render: (value: number, record: any) => (
        <div>
          <Text strong>{value} {record.unit}</Text>
          <Progress 
            percent={Math.round((value / record.min_stock_level) * 100)} 
            size="small" 
            status={value < record.min_stock_level ? 'exception' : 'normal'}
            showInfo={false}
          />
        </div>
      ),
    },
    {
      title: 'Tồn kho tối thiểu',
      dataIndex: 'min_stock_level',
      key: 'min_stock_level',
      render: (value: number, record: any) => `${value} ${record.unit}`,
    },
    {
      title: 'Trạng thái',
      key: 'status',
      render: (record: any) => {
        const percent = Math.round((record.current_stock / record.min_stock_level) * 100);
        if (percent < 50) {
          return <Tag color="red">Nguy cấp</Tag>;
        } else if (percent < 80) {
          return <Tag color="orange">Cảnh báo</Tag>;
        } else {
          return <Tag color="green">Bình thường</Tag>;
        }
      },
    },
  ];

  const expiringColumns = [
    {
      title: 'Tên thuốc',
      dataIndex: 'medicine_name',
      key: 'medicine_name',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>Lô: {record.lot_number}</div>
        </div>
      ),
    },
    {
      title: 'Hạn sử dụng',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Số ngày còn lại',
      dataIndex: 'days_until_expiry',
      key: 'days_until_expiry',
      render: (days: number) => (
        <Tag color={days <= 30 ? 'red' : days <= 90 ? 'orange' : 'green'}>
          {days} ngày
        </Tag>
      ),
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (value: number, record: any) => `${value} ${record.unit}`,
    },
  ];



  // Hiển thị lỗi nếu có
  if (dashboardError || lowStockError || expiringError || categoriesError || revenueError) {
    console.error('API Errors:', {
      dashboard: dashboardError,
      lowStock: lowStockError,
      expiring: expiringError,
      categories: categoriesError,
      revenue: revenueError
    });
  }

  return (
    <ConfigProvider locale={locale}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title
                level={2}
                className={styles.headerTitle}
                style={{ textAlign: 'left', width: '100%' }}
              >
                Dashboard
              </Title>
              <Text
                type="secondary"
                className={styles.headerSubtitle}
                style={{ display: 'block', textAlign: 'left' }}
              >
                Tổng quan hệ thống quản lý kho
              </Text>
            </Col>
            <Col>
              {/* Desktop Controls */}
              <Space className={`${styles.headerControls} ${styles.desktopControls}`}>
                <RangePicker 
                  value={dateRange}
                  onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                  style={{ width: 250 }}
                />
                <Select
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                  style={{ width: 150 }}
                  placeholder="Chọn danh mục"
                >
                  <Option value="all">Tất cả</Option>
                  {categoriesData?.data?.map((cat: any) => (
                    <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                  ))}
                </Select>
                <Tooltip title="Làm mới dữ liệu">
                  <Button 
                    icon={<i className="fas fa-sync-alt" />} 
                    onClick={() => window.location.reload()}
                    className={styles.actionButton}
                  >
                    Làm mới
                  </Button>
                </Tooltip>
                <Tooltip title="Xuất báo cáo">
                  <Button 
                    type="primary" 
                    icon={<i className="fas fa-download" />}
                    className={styles.primaryButton}
                  >
                    Xuất báo cáo
                  </Button>
                </Tooltip>
              </Space>

              {/* Mobile Controls */}
              <Space className={`${styles.headerControls} ${styles.mobileControls}`}>
                <Dropdown
                  overlay={
                    <Menu>
                      <Menu.Item key="date-range" style={{ padding: '12px' }}>
                        <div style={{ marginBottom: '8px' }}>
                          <Text strong>Chọn khoảng thời gian:</Text>
                        </div>
                        <RangePicker 
                          value={dateRange}
                          onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                          style={{ width: '100%' }}
                        />
                      </Menu.Item>
                      <Menu.Item key="category" style={{ padding: '12px' }}>
                        <div style={{ marginBottom: '8px' }}>
                          <Text strong>Chọn danh mục:</Text>
                        </div>
                        <Select
                          value={selectedCategory}
                          onChange={setSelectedCategory}
                          style={{ width: '100%' }}
                          placeholder="Chọn danh mục"
                        >
                          <Option value="all">Tất cả</Option>
                          {categoriesData?.data?.map((cat: any) => (
                            <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                          ))}
                        </Select>
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item key="refresh" icon={<i className="fas fa-sync-alt" />} onClick={() => window.location.reload()}>
                        Làm mới
                      </Menu.Item>
                      <Menu.Item key="export" icon={<i className="fas fa-download" />}>
                        Xuất báo cáo
                      </Menu.Item>
                    </Menu>
                  }
                  trigger={['click']}
                  open={filterDropdownVisible}
                  onOpenChange={setFilterDropdownVisible}
                  placement="bottomRight"
                >
                  <Button icon={<i className="fas fa-filter" />}>
                    Bộ lọc
                  </Button>
                </Dropdown>
                
                <Button icon={<i className="fas fa-sync-alt" />} onClick={() => window.location.reload()}>
                  Làm mới
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {getSummaryStats()}

        {/* Charts Row */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          {/* Stock Trend Chart */}
          <Col xs={24} lg={12}>
            <Card 
              title={
                <div className={styles.chartCardHeader}>
                  <i className="fas fa-chart-line" />
                  <span className={styles.chartCardTitle}>Xu hướng tồn kho</span>
                </div>
              }
              extra={
                <div className={styles.chartCardExtra}>
                  <Button type="link" icon={<i className="fas fa-chart-bar" />}>Chi tiết</Button>
                </div>
              }
              className={styles.chartCard}
            >
              <div className={styles.chartContainer}>
                {dashboardLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin size="large" />
                  </div>
                ) : stockTrendData.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#999', padding: '40px 0' }}>
                    Không có dữ liệu xu hướng tồn kho trong khoảng thời gian đã chọn
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={stockTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend 
                        formatter={(value) => {
                          const legendMap: { [key: string]: string } = {
                            'Tồn kho': 'Tồn kho',
                            'Nhập kho': 'Nhập kho',
                            'Xuất kho': 'Xuất kho'
                          };
                          return legendMap[value] || value;
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="Tồn kho" 
                        stackId="1" 
                        stroke="#1890ff" 
                        fill="#1890ff" 
                        fillOpacity={0.3} 
                      />
                      <Bar dataKey="Nhập kho" fill="#52c41a" />
                      <Bar dataKey="Xuất kho" fill="#ff4d4f" />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>
          </Col>

          {/* Monthly Revenue Chart */}
          <Col xs={24} lg={12}>
            <Card 
              title={
                <div className={styles.chartCardHeader}>
                  <i className="fas fa-dollar-sign" />
                  <span className={styles.chartCardTitle}>Doanh thu & Chi phí theo tháng</span>
                </div>
              }
              className={styles.chartCard}
            >
              <div className={styles.chartContainer}>
                {revenueLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin size="large" />
                  </div>
                ) : monthlyRevenue.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#999', padding: '40px 0' }}>
                    Không có dữ liệu doanh thu
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={monthlyRevenue}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <RechartsTooltip formatter={(value: any, name: any) => [
                        new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value),
                        name
                      ]} 
                      labelFormatter={(label) => {
                        // Chuyển đổi format tháng từ YYYY-MM sang MM/YYYY
                        if (typeof label === 'string' && label.includes('-')) {
                          const [year, month] = label.split('-');
                          return `Tháng ${month}/${year}`;
                        }
                        return label;
                      }}
                      />
                      <Legend 
                        formatter={(value) => {
                          const legendMap: { [key: string]: string } = {
                            'Doanh thu': 'Doanh thu',
                            'Chi phí hàng hóa': 'Chi phí hàng hóa',
                            'Chi phí vận hành': 'Chi phí vận hành',
                            'Lợi nhuận': 'Lợi nhuận'
                          };
                          return legendMap[value] || value;
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        stackId="1" 
                        stroke="#1890ff" 
                        fill="#1890ff" 
                        fillOpacity={0.3} 
                        name="Doanh thu"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="stock_in_expenses" 
                        stackId="1" 
                        stroke="#faad14" 
                        fill="#faad14" 
                        fillOpacity={0.3} 
                        name="Chi phí hàng hóa"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="operating_expenses" 
                        stackId="1" 
                        stroke="#ff7875" 
                        fill="#ff7875" 
                        fillOpacity={0.3} 
                        name="Chi phí vận hành"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="profit" 
                        stroke="#52c41a" 
                        strokeWidth={3}
                        name="Lợi nhuận"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>
          </Col>
        </Row>



        {/* Tables Row */}
        <Row gutter={[16, 16]}>
          {/* Low Stock Table */}
          <Col xs={24} lg={12}>
            <div ref={lowStockRef} />
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-arrow-down" style={{ color: '#faad14' }} />
                  <span>Thuốc tồn kho thấp</span>
                  <Badge count={lowStockData?.data?.filter((item: any) => item.current_stock <= item.min_stock_level).length || 0} style={{ backgroundColor: '#faad14' }} />
                </div>
              }
              extra={
                <Button type="link" size="small" onClick={handleViewAllLowStock}>
                  Xem tất cả
                </Button>
              }
              className={styles.tableCard}
            >
              <Table
                columns={lowStockColumns}
                dataSource={lowStockData?.data?.filter((item: any) => item.current_stock <= item.min_stock_level) || []}
                pagination={false}
                size="small"
                scroll={{ y: 200 }}
                loading={lowStockLoading}
              />
            </Card>
          </Col>

          {/* Expiring Table */}
          <Col xs={24} lg={12}>
            <div ref={expiringRef} />
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-arrow-up" style={{ color: '#ff4d4f' }} />
                  <span>Thuốc sắp hết hạn</span>
                  <Badge count={expiringData?.data?.length || 0} style={{ backgroundColor: '#ff4d4f' }} />
                </div>
              }
              extra={
                <Button type="link" size="small">
                  Xem tất cả
                </Button>
              }
              className={styles.tableCard}
            >
              <Table
                columns={expiringColumns}
                dataSource={expiringData?.data || []}
                pagination={false}
                size="small"
                scroll={{ y: 200 }}
                loading={expiringLoading}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </ConfigProvider>
  );
};

export default DashboardPage; 